<template lang="pug">
  v-dialog(:value="value" @input="updateValue" max-width="468")
    v-card
      v-card-title.headline.justify-space-between
        | {{title}}
      v-card-text
        v-form(ref="form")
          div.license-types
            div.license-types__label License type
            div.license-types__row
              v-checkbox.mr-4(
                v-model="model.is_auto"
                label="Auto"
                :error="formErrors.hasError('licenseTypes')"
                @change="formErrors.validField('licenseTypes')"
              )
              v-checkbox.mr-4(
                v-model="model.is_motor"
                label="Motor"
                :error="formErrors.hasError('licenseTypes')"
                @change="formErrors.validField('licenseTypes')"
              )
              v-checkbox(
                v-model="model.is_scooter"
                label="Scooter"
                :error="formErrors.hasError('licenseTypes')"
                @change="formErrors.validField('licenseTypes')"
              )
            div.license-types__error(v-if="formErrors.hasError('licenseTypes')")
              | {{ formErrors.fields.licenseTypes || 'Please select at least one license type' }}
          v-text-field(
            v-model="model.name"
            label='Subchapter name'
            :rules="[RULES.REQUIRED]"
            :error="formErrors.hasError('name')"
            :error-messages="formErrors.fields.name"
          )
          v-text-field(
            v-model="model.description"
            label='Description (optional)'
            :error="formErrors.hasError('description')"
            :error-messages="formErrors.fields.description"
          )
          v-text-field(
            v-model="model.order"
            type="number"
            label='Order (optional)'
            :error="formErrors.hasError('order')"
            :error-messages="formErrors.fields.order"
          )
          v-autocomplete(
            label='Chapter'
            placeholder="Choose Chapter"
            v-model="model.category"
            :items="filteredChapterCategories"
            item-value="id"
            item-text="name"
          )
          .tenant-checkboxes
            h4 Tenants
            v-checkbox(
              v-model="model.is_wiki"
              label="Wiki"
              :error="formErrors.hasError('is_wiki')"
              :error-messages="formErrors.fields.is_wiki"
            )
            v-checkbox(
              v-model="model.is_les"
              label="Les"
              :error="formErrors.hasError('is_les')"
              :error-messages="formErrors.fields.is_les"
            )
            v-checkbox(
              v-model="model.is_online"
              label="Online"
              :error="formErrors.hasError('is_online')"
              :error-messages="formErrors.fields.is_online"
            )
          v-checkbox(
            v-show="editMode"
            v-model="model.publicVisible"
            label="Is subchapter public visible"
            :readonly="disableChapterPublishToggle"
            @mouseup="togglePublicVisible"
          )
          .d-flex.mt-10
            template(v-if="!editMode")
              v-btn(color="primary" outlined @click="closeModal").ml-auto.mr-3 Cancel
              v-btn(color="primary" depressed @click="addItem") Add
            template(v-if="editMode")
              v-btn(color="primary" outlined @click="showDeleteConfirmationModal").mr-3 Delete
              v-btn(color="primary" outlined @click="closeModal").ml-auto.mr-3 Cancel
              v-btn(color="primary" depressed @click="updateItem") Save
    confirmation-dialog(
      v-model="deleteConfirmationModal"
      title="Are you sure to delete this subchapter?"
      @okBtnClicked="deleteItem"
    )
</template>

<script>
import { REQUIRED } from "@/util/validation-rules";
import FormErrors from "@/util/form-errors"
import ErrorMixin from "@/mixins/errors.mixin"
import ChapterModel from "@/app/exercise/modules/trainings/core/models/chapterModel"
import ChapterSvc from "@/app/exercise/modules/trainings/core/chapter-service"
import {showSnackbar} from "@/util"

export default {
  components: {
    confirmationDialog: () => import('@/components/global/ConfirmationDialog.vue'),
  },
  props: {
    editMode: {
      default: false,
      type: Boolean,
    },
    itemModel: {
      default: null,
      type: Object,
    },
    value: {
      type: Boolean,
    },
  },

  mixins: [ErrorMixin],

  computed: {
    title() {
      if (this.editMode) {
        return 'Edit Subchapter'
      }
      return 'Add new Subchapter'
    },
    disableChapterPublishToggle() {
      return false;
      // return !this.model.publicVisible && this.model.questions.length === 0;
    },
    filteredChapterCategories() {
      const items = this.$store.state.chapterCategories.items;
      if (!items) return [];
      return items.filter(item => item.name);
    }
  },

  data: () => ({
    svc: null,
    formErrors: null,
    model: new ChapterModel(),
    deleteConfirmationModal: false,
    RULES: {
      REQUIRED,
    },
  }),

  created() {
    this.init()
  },

  watch: {
    value(isModalOpen) {
      if(isModalOpen) {
        this.setModelFromProp();
      } else {
        this.formReset();
      }
    },
  },

  methods: {
    init() {
      this.svc = new ChapterSvc()
      this.formErrors = new FormErrors()
    },
    formReset() {
      this.model = new ChapterModel();
      this.formErrors = new FormErrors();
      this.$refs.form.reset();
    },
    togglePublicVisible() {
      if (this.disableChapterPublishToggle) this.$notify({text: `Can't publish subchapter with no questions in it`, type: 'error'});
    },
    setModelFromProp() {
      Object.assign(this.model, this.itemModel );
    },
    updateValue(value) {
      this.$emit('input', value)
    },
    closeModal() {
      this.$emit('closeModal')
    },
    showDeleteConfirmationModal(item) {
      this.deleteConfirmationModal = true
      this.deletingItem = item
    },
    async addItem() {
      try {
        if (!this.$refs.form.validate() || !this.model.validate(this.formErrors) ) {
          showSnackbar('Invalid fields', {color: 'error'})
          return false;
        }
        let res = await this.svc.create(this.model)
        this.$emit('itemCreated');
        this.closeModal();
      } catch (error) {
        this.processErrorWithForm(error, {
          redirectName: this.$ROUTER_NAMES.LOGIN_EXERCISE,
          formErrors: this.formErrors, 
          apiAttributes: this.model._apiAttributes
        });
      }
    },
    async updateItem() {
      const isModelChanged = JSON.stringify(this.model) !== JSON.stringify(this.itemModel);
      if (!isModelChanged) {
        this.closeModal();
        return;
      }
      try {
        if (!this.$refs.form.validate() || !this.model.validate(this.formErrors) ) {
          showSnackbar('Invalid fields', {color: 'error'})
          return false;
        }
        let res = await this.svc.put(this.model.id, this.model)
        this.$emit('itemUpdated');
        this.closeModal();
      } catch (error) {
        this.processErrorWithForm(error, {
          redirectName: this.$ROUTER_NAMES.LOGIN_EXERCISE,
          formErrors: this.formErrors, 
          apiAttributes: this.model._apiAttributes
        });
      }
    },
    async deleteItem() {
      try {
        let res = await this.svc.remove(this.model.id)
        this.$emit('itemDeleted');
        this.closeModal();
      } catch (error) {
        this.processErrorWithForm(error, {
          redirectName: this.$ROUTER_NAMES.LOGIN_EXERCISE,
          formErrors: this.formErrors, 
          apiAttributes: this.model._apiAttributes
        });
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.v-btn::v-deep {
  .v-btn__content {
    max-width: 100%;
  }
}
.thumbnail {
  max-width: 100%;
  border-radius: 50%;
}

.license-types {
  &__label {
    color: $label-color;
    font-weight: 400;
    font-size: 12px;
    margin-bottom: 2px;
  }

  &__row {
    display: flex;
    align-items: center;

    &::v-deep {
      label {
        font-weight: 400;
        margin-bottom: 0;
      }

      .v-input {
        margin-top: 0;
      }

      .v-messages {
        display: none;
      }
    }
  }

  &__error {
    color: $error-color;
    font-size: 12px;
    margin-bottom: 24px;
    margin-top: -4px;
  }
}
</style>
